import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetDevice } from '../../../../api/routes/devices'
import {
    useGetServiceRecords,
    useGetServiceSchedules,
    useGetServiceTypes,
} from '../../../../api/routes/services'
import { authClient, hasPermission } from '../../../../lib/auth-client'
import { useBusinessModuleCheck } from '../../../../api/routes/modules'
import { Button } from '../../../../components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table'
import Header from '../header'
import LogServiceModal from './log-service-modal'
import ServiceTypesSection from './service-types-section'

const DeviceServicesRoute = () => {
    const { id: deviceId } = useParams()
    const { data: device, isLoading: deviceLoading } = useGetDevice(deviceId as string)
    const { data: moduleAccess, isLoading: moduleLoading } = useBusinessModuleCheck(
        device?.business?.id || null,
        'service'
    )
    const hasService = !!moduleAccess?.hasAccess

    const { data: records, isLoading: recordsLoading } = useGetServiceRecords(
        hasService ? (deviceId as string) : ''
    )
    const { data: schedules, isLoading: schedulesLoading } = useGetServiceSchedules(
        hasService ? (deviceId as string) : ''
    )
    const { data: serviceTypes } = useGetServiceTypes(
        hasService ? (deviceId as string) : ''
    )
    const { data: session, isPending: sessionLoading } = authClient.useSession()
    const user = session?.user as any

    const [showLogModal, setShowLogModal] = useState(false)
    const [filterTypeId, setFilterTypeId] = useState<string>('all')

    const isLoading = deviceLoading || sessionLoading || moduleLoading || (hasService ? recordsLoading || schedulesLoading : false)

    if (isLoading || !user) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 w-48 bg-muted rounded" />
                    <div className="h-10 w-full sm:w-96 bg-muted rounded-lg" />
                    <div className="h-64 bg-muted rounded-lg" />
                </div>
            </div>
        )
    }

    if (!device || !deviceId) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-muted-foreground">Device not found</p>
                </div>
            </div>
        )
    }

    if (!hasService) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <Header deviceId={deviceId} name={device?.name} businessId={device?.business?.id} />
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-sm font-medium text-foreground mb-1">Module not available</p>
                    <p className="text-xs text-muted-foreground">
                        The Service module is not enabled for your business.
                        Contact your administrator to enable this feature.
                    </p>
                </div>
            </div>
        )
    }

    // Schedule summary counts
    const scheduleList = schedules || []
    const overdueCount = scheduleList.filter((s: any) => s.status === 'overdue').length
    const dueSoonCount = scheduleList.filter((s: any) => s.status === 'due_soon').length
    const upcomingCount = scheduleList.filter((s: any) => s.status === 'upcoming').length

    // Filter and sort records
    const recordsList = records || []
    const filteredRecords = filterTypeId === 'all'
        ? recordsList
        : recordsList.filter((r: any) => r.serviceType?.id === filterTypeId)
    const sortedRecords = [...filteredRecords].sort(
        (a: any, b: any) => new Date(b.servicedAt).getTime() - new Date(a.servicedAt).getTime()
    )

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <Header deviceId={deviceId} name={device?.name} businessId={device?.business?.id} />

            {/* Schedule Summary */}
            {scheduleList.length > 0 && (
                <div className="rounded-lg border bg-card mb-6">
                    <div className="px-6 py-4 border-b">
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                            Service Schedule
                        </h3>
                    </div>
                    <div className="px-6 py-4">
                        <div className="flex flex-wrap gap-3">
                            {overdueCount > 0 && (
                                <span className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                                    {overdueCount} Overdue
                                </span>
                            )}
                            {dueSoonCount > 0 && (
                                <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                                    {dueSoonCount} Due Soon
                                </span>
                            )}
                            {upcomingCount > 0 && (
                                <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                                    {upcomingCount} Upcoming
                                </span>
                            )}
                            {overdueCount === 0 && dueSoonCount === 0 && upcomingCount === 0 && (
                                <span className="text-xs text-muted-foreground">All services up to date</span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Service History */}
            <div className="rounded-lg border bg-card mb-6">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        Service History
                    </h3>
                    <div className="flex items-center gap-3">
                        <Select value={filterTypeId} onValueChange={setFilterTypeId}>
                            <SelectTrigger className="w-[180px] h-8 text-xs">
                                <SelectValue placeholder="Filter by type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Types</SelectItem>
                                {(serviceTypes || []).map((type: any) => (
                                    <SelectItem key={type.id} value={type.id}>
                                        {type.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button size="sm" onClick={() => setShowLogModal(true)}>
                            Log Service
                        </Button>
                    </div>
                </div>
                {sortedRecords.length === 0 ? (
                    <div className="p-12 text-center">
                        <p className="text-sm text-muted-foreground">
                            No service records yet. Log your first service to get started.
                        </p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Cost</TableHead>
                                <TableHead>Meter Reading</TableHead>
                                <TableHead>Technician</TableHead>
                                <TableHead>Notes</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedRecords.map((record: any) => (
                                <TableRow key={record.id}>
                                    <TableCell>
                                        {new Date(record.servicedAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {record.serviceType?.name || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {record.cost != null ? `$${Number(record.cost).toFixed(2)}` : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {record.meterValue != null
                                            ? Number(record.meterValue).toLocaleString()
                                            : '-'}
                                    </TableCell>
                                    <TableCell>{record.performedBy || '-'}</TableCell>
                                    <TableCell className="max-w-[200px] truncate text-muted-foreground">
                                        {record.notes || '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            {/* Service Types Management - admin only */}
            {hasPermission(user.role, { device: ['update'] }) && (
                <ServiceTypesSection deviceId={deviceId} />
            )}

            <LogServiceModal
                open={showLogModal}
                onOpenChange={setShowLogModal}
                deviceId={deviceId}
            />
        </div>
    )
}

export { DeviceServicesRoute }
